import { ref, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import DOMPurify from 'dompurify';
import store from '@/store';
import { config } from '@/config.js';
import apiService from '@/services/notificaciones-boletines';
import { useToast } from '@/composables/common/useToast';

/**
 * Formatea una fecha ISO como dd-mm-yyyy hh:mm (mismo formato que el widget
 * legacy de notificaciones).
 * @param {string} fecha
 * @returns {string}
 */
export function formatNotificacionFecha(fecha) {
    if (!fecha) return '';
    const date = new Date(fecha);
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const anio = date.getFullYear();
    const horas = String(date.getHours()).padStart(2, '0');
    const minutos = String(date.getMinutes()).padStart(2, '0');
    return `${dia}-${mes}-${anio} ${horas}:${minutos}`;
}

function hasMarketingToken() {
    const teatro = store.getters.getTeatro;
    return !!(teatro && teatro.marketing_api_token);
}

/**
 * Clave única por origen: backend y comunicados pueden compartir id numérico.
 * @param {object} notificacion
 * @returns {string}
 */
function claveNotificacion(notificacion) {
    return (notificacion.tipo === 'comunicados' ? 'com-' : 'back-') + notificacion.id;
}

/**
 * ¿La notificación ya está en la lista? El push de Echo y el fetch pueden
 * traer la misma.
 *
 * @param {Array<object>} lista
 * @param {object} notificacion
 * @returns {boolean}
 */
export function estaNotificacionEnLista(lista = [], notificacion = {}) {
    const clave = claveNotificacion(notificacion);
    return lista.some(item => claveNotificacion(item) === clave);
}

/**
 * Notificaciones del header admin (misma lógica que el widget legacy):
 * backend `back.notificaciones.*` + comunicados del servicio de marketing
 * (cuando el teatro tiene token) + canal echo `manager.user.{id}`.
 */
export function useAdminNotifications() {
    const toast = useToast();

    const notificaciones = ref([]);
    const loading = ref(false);
    const lastFetchAt = ref(0);
    // Tick que solo sube cuando hay notificaciones realmente nuevas; el
    // widget lo usa para balancear la campana.
    const nuevasNotificaciones = ref(0);
    let primeraCargaHecha = false;
    let echoChannel = null;

    // Evita ráfagas al abrir/cerrar la campana o al cambiar de pestaña.
    const REFRESH_COOLDOWN_MS = 5000;

    const unreadCount = computed(() => notificaciones.value.length);

    async function fetchNotificaciones() {
        lastFetchAt.value = Date.now();
        loading.value = true;
        const clavesPrevias = new Set(notificaciones.value.map(claveNotificacion));
        const esPrimeraCarga = !primeraCargaHecha;
        try {
            const res = await store.dispatch(config.getVuexAlias('back.notificaciones.get'), { filters: {} });
            notificaciones.value = (res && res.data && res.data.data) || [];

            if (hasMarketingToken()) {
                try {
                    const comunicados = await apiService.getNotificaciones();
                    (comunicados.data || []).forEach(notificacion => {
                        notificaciones.value.push({
                            id: notificacion.id,
                            data: { mensaje: notificacion.mensaje },
                            created_at: notificacion.created_at,
                            tipo: 'comunicados'
                        });
                    });
                } catch (error) {
                    toast.error((error.response && error.response.data && error.response.data.message) || 'Error al cargar las notificaciones');
                }
            }
        } catch (error) {
            notificaciones.value = [];
        } finally {
            loading.value = false;
            primeraCargaHecha = true;

            // La carga inicial no debe balancear la campana; los refrescos que
            // traen algo nuevo, sí.
            if (!esPrimeraCarga && notificaciones.value.some(item => !clavesPrevias.has(claveNotificacion(item)))) {
                nuevasNotificaciones.value += 1;
            }
        }
    }

    async function marcarComoLeida(notificacion) {
        if (notificacion.tipo === 'comunicados' && hasMarketingToken()) {
            try {
                await apiService.marcarNotificacionLeida(notificacion.id);
                await fetchNotificaciones();
            } catch (error) {
                toast.error(error.response && error.response.data && error.response.data.message);
            }
            return;
        }

        try {
            await store.dispatch(config.getVuexAlias('back.notificaciones.marcar-como-leida'), {
                filters: {},
                notificacion_id: notificacion.id
            });
            toast.success('Notificación leída correctamente');
            await fetchNotificaciones();
        } catch (error) {
            toast.error(error.response && error.response.data && error.response.data.message);
        }
    }

    async function marcarComoLeidas() {
        if (hasMarketingToken()) {
            try {
                await apiService.marcarNotificacionLeidaMasiva();
            } catch (error) {
                // Se continúa con las notificaciones del backend.
            }
        }

        try {
            await store.dispatch(config.getVuexAlias('back.notificaciones.marcar-como-leidas'), { filters: {} });
            notificaciones.value = [];
            toast.success('Notificaciónes leídas correctamente');
        } catch (error) {
            toast.error(error.response && error.response.data && error.response.data.message);
        }
    }

    /** Sanitiza el HTML de las notificaciones antes de renderizarlas. */
    function sanitize(mensaje) {
        return DOMPurify.sanitize(mensaje || '');
    }

    function subscribeEcho() {
        const instance = getCurrentInstance();
        const echo = instance && instance.proxy && instance.proxy.$echo;
        const user = store.getters.getUser;
        if (!echo || !user || !user.id) return;
        try {
            echoChannel = echo.channel('manager.user.' + user.id);
            echoChannel.on('new.notification', notificacion => {
                // El fetch y el push pueden traer la misma notificación.
                if (!estaNotificacionEnLista(notificaciones.value, notificacion)) {
                    notificaciones.value.push(notificacion);
                    nuevasNotificaciones.value += 1;
                }
            });
        } catch (error) {
            echoChannel = null;
        }
    }

    /**
     * Refresco bajo demanda: al abrir el panel de la campana y al volver a la
     * pestaña. El header vive en el layout y no se remonta al navegar por el
     * SPA, así que sin esto el contador solo cambiaba al recargar.
     */
    async function refreshNotificaciones() {
        if (Date.now() - lastFetchAt.value < REFRESH_COOLDOWN_MS) return;
        await fetchNotificaciones();
    }

    function handleVisibilityChange() {
        if (document.visibilityState === 'visible') refreshNotificaciones();
    }

    function stopEcho() {
        if (echoChannel) {
            try {
                echoChannel.stopListening('new.notification');
            } catch (error) {
                // Canal ya cerrado.
            }
            echoChannel = null;
        }
    }

    if (getCurrentInstance()) {
        onMounted(() => {
            fetchNotificaciones();
            subscribeEcho();
            document.addEventListener('visibilitychange', handleVisibilityChange);
        });
        onUnmounted(() => {
            stopEcho();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        });
    }

    return {
        notificaciones,
        loading,
        unreadCount,
        nuevasNotificaciones,
        fetchNotificaciones,
        refreshNotificaciones,
        marcarComoLeida,
        marcarComoLeidas,
        sanitize
    };
}
